import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";

import LevelFilter from "./LevelFilter";
import ActionButtons from "./ActionButtons";
import { UserLevel } from "utils/constant";
import { MonthOptions } from "../utils";
import InputRadio from "components/atoms/InputRadio";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";
import FlickIdentification from "services/FlickIdentification";

const FlickIdentificationService = new FlickIdentification();

const isValidYYYY = (v) => v !== undefined && /^\d{4}$/.test(String(v));

const FilterSection = ({
  currentUser,
  selectedLevel,
  setSelectedLevel,
  levelData,
  levelDataOptions,
  setLevelIdValue,
  selectedProvince,
  setSelectedProvince,
  selectedDistrict,
  setSelectedDistrict,
  selectedFasyankes,
  setSelectedFasyankes,
  handleApplyFilter,
  tableRef,
  showData,
  showFilter,
  setShowFilter,
  levelIdValue,
  reportName = "LAPORAN_REKAP_MASTER",
}) => {
  const [year, setYear] = useState("");
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [larvaIdentifications, setLarvaIdentifications] = useState([]);
  const [selectedDesa, setSelectedDesa] = useState(""); // urbanvillage CODE
  const [selectedNamaTp, setSelectedNamaTp] = useState("");

  useEffect(() => {
    // Load breeding-place identifications for the selected fasyankes so the
    // Desa and TP-name filters can be chosen before applying the report.
    const loadOptions = async () => {
      if (selectedLevel !== UserLevel.Fasyankes || !selectedFasyankes) {
        setLarvaIdentifications([]);
        setSelectedDesa("");
        setSelectedNamaTp("");
        return;
      }
      try {
        const resp = await FlickIdentificationService.getAll({
          page: 1,
          limit: 9999,
          searchBy: "fasyankes_id",
          searchValue: selectedFasyankes, // value is "code|name", matches stored fasyankes_id exactly
        });
        setLarvaIdentifications(resp?.vector_larva_identifications || []);
      } catch (err) {
        console.error(err);
        setLarvaIdentifications([]);
      }
      // Reset dependent selections whenever the fasyankes changes.
      setSelectedDesa("");
      setSelectedNamaTp("");
    };
    loadOptions();
  }, [selectedFasyankes, selectedLevel]);

  useEffect(() => {
    if (currentUser.level === UserLevel.Fasyankes) {
      setLevelIdValue(currentUser?.fasyankes_id);
    } else if (currentUser.level === UserLevel.District) {
      setSelectedFasyankes("");
      setLevelIdValue(currentUser?.district_id);
    } else if (currentUser.level === UserLevel.Province) {
      setSelectedDistrict("");
      setSelectedFasyankes("");
      setLevelIdValue(currentUser?.province_id);
    } else if (currentUser.level === UserLevel.National) {
      setSelectedProvince("");
      setSelectedDistrict("");
      setSelectedFasyankes("");
    } else {
      setLevelIdValue("");
    }
  }, [selectedLevel]);

  const levelLabel = useMemo(
    () =>
      selectedLevel === UserLevel.National
        ? UserLevel.National
        : levelIdValue?.split("|")[1],
    [selectedLevel, levelIdValue]
  );

  const isLevelChosen = () =>
    (selectedLevel === UserLevel.Fasyankes && !!selectedFasyankes) ||
    (selectedLevel === UserLevel.District && !!selectedDistrict) ||
    (selectedLevel === UserLevel.Province && !!selectedProvince) ||
    selectedLevel === UserLevel.National;

  const isPeriodValid = () => isValidYYYY(year);

  const isEnableButton = () => isLevelChosen() && isPeriodValid();

  const buildPayload = () => {
    const payload = {
      level: selectedLevel,
      period: "year",
      year: Number(year),
    };

    if (selectedLevel === UserLevel.Fasyankes)
      payload.level_id = selectedFasyankes;
    if (selectedLevel === UserLevel.District)
      payload.level_id = selectedDistrict;
    if (selectedLevel === UserLevel.Province)
      payload.level_id = selectedProvince;

    if (Array.isArray(selectedMonths) && selectedMonths.length > 0) {
      payload.months = selectedMonths;
    }

    // Server-side desa + breeding-place filters (only meaningful at fasyankes level).
    if (selectedLevel === UserLevel.Fasyankes) {
      if (selectedDesa) payload.urbanvillage = selectedDesa;
      if (selectedNamaTp) payload.nama_tp = selectedNamaTp;
    }

    return payload;
  };

  const exportToExcel = () => {
    const table = tableRef.current;
    const wb = XLSX.utils.table_to_book(table, { sheet: "Sheet 1" });

    const monthSuffix =
      selectedMonths.length > 0 ? `_M${selectedMonths.join("-")}` : "";
    const periodSuffix = `Y${year}${monthSuffix}`;

    XLSX.writeFile(
      wb,
      `${reportName}_${levelLabel}_${periodSuffix}_${moment().format(
        "DDMMYYYY"
      )}.xlsx`
    );
  };

  // Distinct desa options from the loaded identifications (value = urbanvillage code).
  const desaFilterOptions = Array.from(
    new Map(
      (larvaIdentifications || [])
        .filter((r) => r?.urbanvillage_id)
        .map((r) => {
          const code = String(r.urbanvillage_id).split("|")[0];
          const name = String(r.urbanvillage_id).split("|")[1] || code;
          return [code, { label: name, value: code }];
        })
    ).values()
  );

  // TP-name options, narrowed to the selected desa when one is chosen.
  const tpFilterOptions = Array.from(
    new Set(
      (larvaIdentifications || [])
        .filter(
          (r) =>
            !selectedDesa ||
            String(r?.urbanvillage_id || "").split("|")[0] === selectedDesa
        )
        .map((r) => r?.name_breeding_place)
        .filter(Boolean)
    )
  ).map((t) => ({ label: t, value: t }));

  return (
    <div className="filter">
      <div className="filter-header">
        <p>FILTER</p>
        <button
          className="btn_toggle_filter"
          onClick={() => setShowFilter(!showFilter)}
        >
          {showFilter ? <span>&#9650;</span> : <span>&#9660;</span>}
        </button>
      </div>

      {showFilter && (
        <div className="filter-body">
          <div className="filter-item">
            <div className="filter-label">
              <p>Periode</p>
            </div>
            <div className="filter-field">
              <InputRadio
                isRequired={true}
                options={[{ label: "Tahunan", value: "year" }]}
                onChange={() => {}}
                selectedOption="year"
              />

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "1.2rem",
                  marginTop: 12,
                }}
              >
                <div style={{ flex: "1" }}>
                  <InputDate
                    value={year ? moment(String(year), "YYYY") : null}
                    onChange={(e) => {
                      const selectedYear = moment(e).format("YYYY");
                      setYear(Number(selectedYear));
                    }}
                    placeholder={"Pilih Tahun"}
                    dateFormat={"YYYY"}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <InputSelect
                    isMultipleSelect={true}
                    placeholder={"Pilih Bulan (Opsional)"}
                    data={MonthOptions}
                    onChange={(selected) => {
                      const vals = selected
                        ? selected.map((item) => Number(item.value))
                        : [];
                      setSelectedMonths(vals);
                    }}
                    value={MonthOptions.filter((item) =>
                      selectedMonths.includes(item.value)
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="filter-item">
            <div className="filter-label">
              <p>Level</p>
            </div>
            <div className="filter-field">
              <LevelFilter
                currentUser={currentUser}
                selectedLevel={selectedLevel}
                setSelectedLevel={setSelectedLevel}
                levelData={levelData}
                levelDataOptions={levelDataOptions}
                setLevelIdValue={setLevelIdValue}
                selectedProvince={selectedProvince}
                setSelectedProvince={setSelectedProvince}
                selectedDistrict={selectedDistrict}
                setSelectedDistrict={setSelectedDistrict}
                selectedFasyankes={selectedFasyankes}
                setSelectedFasyankes={setSelectedFasyankes}
              />
            </div>
          </div>

          {selectedLevel === UserLevel.Fasyankes && selectedFasyankes && (
            <div className="filter-item">
              <div className="filter-label">
                <p>Desa & Tempat Perindukan</p>
              </div>
              <div className="filter-field">
                <div style={{ display: "flex", flexDirection: "row", gap: "1.2rem" }}>
                  <div style={{ flex: "1" }}>
                    <InputSelect
                      label="Desa"
                      placeholder="Semua Desa"
                      isClearable
                      data={desaFilterOptions}
                      value={
                        desaFilterOptions.find((o) => o.value === selectedDesa) || ""
                      }
                      onChange={(e) => {
                        // Changing desa resets the dependent TP-name filter.
                        setSelectedDesa(e ? e.value : "");
                        setSelectedNamaTp("");
                      }}
                    />
                  </div>
                  <div style={{ flex: "1" }}>
                    <InputSelect
                      label="Nama Tempat Perindukan"
                      placeholder="Semua Tempat Perindukan"
                      isClearable
                      data={tpFilterOptions}
                      value={
                        tpFilterOptions.find((o) => o.value === selectedNamaTp) || ""
                      }
                      onChange={(e) => setSelectedNamaTp(e ? e.value : "")}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="filter-item">
            <div className="filter-field">
              <ActionButtons
                onApply={() => handleApplyFilter(buildPayload())}
                onExport={exportToExcel}
                isExportVisible={showData}
                isDisabled={!isEnableButton()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterSection;
